import { closestCenter, DndContext, type DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { HeaderOrderUpsertDTO } from '@tempo-io/roadmap-dto';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '@/common/components/base-dialog/BaseDialog';
import { createDragSensors } from '@/features/settings/item-types/components/lib/utils';
import { hasOrderChanged, orderHeaders, type ReorderableHeader } from './reorderHeadersUtils';
import { SortableHeaderItem } from './SortableHeaderItem';

interface SaveHeaderOrderArgs {
  headerFieldId: string;
  headerOrder: HeaderOrderUpsertDTO;
}

interface ReorderHeadersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  headers: ReorderableHeader[];
  headerFieldId: string;
  parentFieldId: string | null;
  parentHeaderId: string | null;
  orderedHeaderIds: string[] | undefined;
  isPending: boolean;
  onSaveOrder: (args: SaveHeaderOrderArgs) => Promise<unknown>;
}

export const ReorderHeadersDialog = ({
  isOpen,
  onClose,
  headers,
  headerFieldId,
  parentFieldId,
  parentHeaderId,
  orderedHeaderIds,
  isPending,
  onSaveOrder,
}: ReorderHeadersDialogProps) => {
  const { t } = useTranslation();
  const [localHeaders, setLocalHeaders] = useState<ReorderableHeader[]>([]);

  const orderedHeaders = useMemo(
    () => orderHeaders(headers, orderedHeaderIds),
    [headers, orderedHeaderIds],
  );

  useEffect(() => {
    setLocalHeaders(orderedHeaders);
  }, [orderedHeaders]);

  const sensors = createDragSensors();

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setLocalHeaders((items) => {
      const oldIndex = items.findIndex((header) => header.id === active.id);
      const newIndex = items.findIndex((header) => header.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return items;
      }

      return arrayMove(items, oldIndex, newIndex);
    });
  }, []);

  const handleSave = async () => {
    if (!hasOrderChanged(localHeaders, orderedHeaders)) {
      onClose();
      return;
    }
    await onSaveOrder({
      headerFieldId,
      headerOrder: {
        orderedHeaderIds: localHeaders.map((header) => header.id),
        parentFieldHeaderId: parentFieldId,
        parentHeaderId,
      },
    });
    onClose();
  };

  const handleCancel = () => {
    setLocalHeaders(orderedHeaders);
    onClose();
  };

  return (
    <BaseDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleCancel();
      }}
      size="sm"
      title={t(($) => $.views.visualization.reorder.headers)}
      description={t(($) => $.views.visualization.reorder.header.type.description)}
      isPending={isPending}
      primaryAction={{
        label: t(($) => $.common.save),
        onClick: () => {
          void handleSave();
        },
      }}
      secondaryAction={{
        label: t(($) => $.common.cancel),
        onClick: handleCancel,
      }}
    >
      <div className="py-4 max-h-96 overflow-y-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToParentElement, restrictToVerticalAxis]}
          autoScroll={false}
        >
          <SortableContext
            items={localHeaders.map((header) => header.id)}
            strategy={verticalListSortingStrategy}
          >
            {localHeaders.map((header, index) => (
              <SortableHeaderItem key={header.id} header={header} index={index} />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </BaseDialog>
  );
};
